"use client";

import { ArrowRight, Factory, Gauge, Layers } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface DirectLink {
  label: Record<Lang, string>;
  to: string;
}
interface FinderCardData {
  id: string;
  icon: typeof Factory;
  title: Record<Lang, string>;
  blurb: Record<Lang, string>;
  links: DirectLink[];
  mutedLink?: DirectLink;
}
const finderCards: FinderCardData[] = [{
  id: "by-application",
  icon: Factory,
  title: {
    en: "By Application",
    zh: "按应用领域",
    es: "Por aplicación",
    de: "Nach Anwendung",
    fr: "Par application",
    pt: "Por aplicação"
  },
  blurb: {
    en: "Select wire mesh and filter elements matched to industrial process fluids, solids handling, and equipment configurations.",
    zh: "根据工艺介质、固液分离工况及工业设备结构选择适用的金属网与过滤元件。",
    es: "Seleccione mallas de alambre y elementos filtrantes adaptados a fluidos de proceso, manejo de sólidos y configuraciones de equipos.",
    de: "Wählen Sie Drahtgewebe und Filterelemente abgestimmt auf Prozessmedien, Feststoffhandling und Auslegungsvorgaben.",
    fr: "Sélectionnez les toiles métalliques et éléments filtrants adaptés aux fluides de procédé, solides et configurations de vos équipements.",
    pt: "Selecione telas de arame e elementos filtrantes adequados a fluidos de processo, sólidos e configurações de equipamentos."
  },
  links: [{
    to: "/PolymerFiltrationPage",
    label: {
      en: "Polymer Filtration",
      zh: "聚合物熔体过滤",
      es: "Filtración de polímeros",
      de: "Polymerfiltration",
      fr: "Filtration des polymères",
      pt: "Filtração de polímeros"
    }
  }, {
    to: "/WaterLiquidFiltrationPage",
    label: {
      en: "Water & Liquid Filtration",
      zh: "水与液体过滤",
      es: "Filtración de agua y líquidos",
      de: "Wasser- & Flüssigkeitsfiltration",
      fr: "Filtration de l'eau et des liquides",
      pt: "Filtração de água e líquidos"
    }
  }, {
    to: "/FoodAndPharmaceuticalFiltrationPage",
    label: {
      en: "Food & Pharmaceutical Filtration",
      zh: "食品与制药行业过滤",
      es: "Filtración farmacéutica y alimentaria",
      de: "Lebensmittel- & Pharmafiltration",
      fr: "Filtration agroalimentaire et pharmaceutique",
      pt: "Filtração farmacêutica e alimentícia"
    }
  }],
  mutedLink: {
    to: "/ApplicationsPage",
    label: {
      en: "All Applications",
      zh: "全部应用领域",
      es: "Todas las aplicaciones",
      de: "Alle Anwendungen",
      fr: "Toutes les applications",
      pt: "Todas as aplicações"
    }
  }
}, {
  id: "by-precision",
  icon: Gauge,
  title: {
    en: "By Filtration Precision",
    zh: "按过滤精度",
    es: "Por precisión de filtración",
    de: "Nach Filtrationsfeinheit",
    fr: "Par précision de filtration",
    pt: "Por precisão de filtração"
  },
  blurb: {
    en: "Identify suitable wire mesh weaves, micron ratings, aperture dimensions, and mesh count conversions.",
    zh: "查阅适合的编织类型、微米等级、网孔开孔尺寸及目数开孔换算基准。",
    es: "Identifique tipos de tejido, clasificaciones en micras, dimensiones de abertura y conversiones de número de mallas.",
    de: "Ermitteln Sie Webarten, Mikrometer-Bewertungen, Maschenweiten und Umrechnungen von Maschenzahlen.",
    fr: "Identifiez les armures adaptées, seuils de coupure en microns, dimensions d'ouvertures et conversions de maillage.",
    pt: "Identifique tipos de trama, classificações em micra, dimensões de abertura e conversões de contagem de malhas."
  },
  links: [{
    to: "/ApertureMicronRatingChartsPage",
    label: {
      en: "Aperture & Micron Rating Charts",
      zh: "网孔与微米等级对照表",
      es: "Tablas de abertura y clasificación en micras",
      de: "Maschenweiten- & Mikrometer-Tabellen",
      fr: "Tableaux d'ouvertures et de microns",
      pt: "Tabelas de abertura e classificação em micra"
    }
  }, {
    to: "/MeshCountAndAperturePage",
    label: {
      en: "Mesh Count & Aperture",
      zh: "目数与孔径计算关系",
      es: "Número de mallas y abertura",
      de: "Maschenzahl & Maschenweite",
      fr: "Nombre de mailles et ouverture",
      pt: "Contagem de malha e abertura"
    }
  }, {
    to: "/MicronAndFiltrationPage",
    label: {
      en: "Micron Rating & Filtration Accuracy",
      zh: "过滤精度与截留特性",
      es: "Clasificación en micras y precisión",
      de: "Mikrometer-Klassifizierung & Filterfeinheit",
      fr: "Seuil de filtration et précision",
      pt: "Classificação em micra e precisão"
    }
  }]
}, {
  id: "by-material",
  icon: Layers,
  title: {
    en: "By Material",
    zh: "按材料材质",
    es: "Por material",
    de: "Nach Werkstoff",
    fr: "Par matériau",
    pt: "Por material"
  },
  blurb: {
    en: "Review stainless steel alloy compositions, temperature thresholds, and chemical compatibility criteria.",
    zh: "对比不锈钢合金牌号成分、耐温工作区间及耐化学腐蚀介质适应性。",
    es: "Revise composiciones de aleaciones de acero inoxidable, límites térmicos y compatibilidad química.",
    de: "Vergleichen Sie Edelstahllegierungen, Temperaturbereiche und chemische Beständigkeitskriterien.",
    fr: "Consultez les nuances d'acier inoxydable, limites de température et critères de compatibilité chimique.",
    pt: "Analise composições de ligas de aço inoxidável, limites térmicos e compatibilidade química."
  },
  links: [{
    to: "/MaterialsAndGradesPage",
    label: {
      en: "Materials & Grades",
      zh: "材料牌号与规格概述",
      es: "Materiales y calidades",
      de: "Werkstoffe & Güten",
      fr: "Matériaux et nuances",
      pt: "Materiais e graus"
    }
  }, {
    to: "/StainlessSteelGradeSelectionPage",
    label: {
      en: "Stainless Steel Grade Selection",
      zh: "不锈钢牌号选型指南",
      es: "Selección de grados de acero inoxidable",
      de: "Edelstahl-Gütenauswahl",
      fr: "Sélection des nuances d'acier inoxydable",
      pt: "Seleção de graus de aço inoxidável"
    }
  }, {
    to: "/MaterialCompatibilityGuidePage",
    label: {
      en: "Material Compatibility Guide",
      zh: "材料介质相容性指南",
      es: "Guía de compatibilidad de materiales",
      de: "Leitfaden zur Materialverträglichkeit",
      fr: "Guide de compatibilité des matériaux",
      pt: "Guia de compatibilidade de materiais"
    }
  }]
}];
const selectionGuides: DirectLink[] = [{
  to: "/ProductSelectionPage",
  label: {
    en: "Product Selection",
    zh: "产品选型",
    es: "Selección de productos",
    de: "Produktauswahl",
    fr: "Sélection de produits",
    pt: "Seleção de produtos"
  }
}, {
  to: "/FilterMeshSelectionGuidePage",
  label: {
    en: "Filter Mesh Selection Guide",
    zh: "过滤金属网选型指南",
    es: "Guía de selección de mallas filtrantes",
    de: "Filtergewebe-Auswahlleitfaden",
    fr: "Guide de sélection des toiles filtrantes",
    pt: "Guia de seleção de telas filtrantes"
  }
}, {
  to: "/SinteredWireMeshSelectionGuidePage",
  label: {
    en: "Sintered Wire Mesh Selection Guide",
    zh: "烧结丝网选型指南",
    es: "Guía de selección de malla sinterizada",
    de: "Sinterdrahtgewebe-Auswahlleitfaden",
    fr: "Guide de sélection des toiles frittées",
    pt: "Guia de seleção de telas sinterizadas"
  }
}, {
  to: "/WedgeWireScreenSelectionGuidePage",
  label: {
    en: "Wedge Wire Screen Selection Guide",
    zh: "楔形绕丝筛选型指南",
    es: "Guía de selección de tamices de alambre en cuña",
    de: "Spaltsieb-Auswahlleitfaden",
    fr: "Guide de sélection des crépines à fil profilé",
    pt: "Guia de seleção de telas de arame em cunha"
  }
}, {
  to: "/FilterComponentsSelectionGuidePage",
  label: {
    en: "Filter Components Selection Guide",
    zh: "深加工过滤元件选型指南",
    es: "Guía de selección de componentes filtrantes",
    de: "Filterkomponenten-Auswahlleitfaden",
    fr: "Guide de sélection des composants filtrants",
    pt: "Guia de seleção de componentes filtrantes"
  }
}, {
  to: "/DownloadsPage",
  label: {
    en: "Downloads",
    zh: "技术资料下载",
    es: "Descargas",
    de: "Downloads",
    fr: "Téléchargements",
    pt: "Downloads"
  }
}];
export default function ProductFinder() {
  const {
    lang
  } = useLanguage();
  return <section id="productfinder" data-nav="light" className="border-y border-border bg-muted/50 mf-section text-foreground">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-[65ch] mb-10 md:mb-12">
            <h2 className="mf-h2 text-foreground">
              {pick(lang, {
              en: "Find the Right Product",
              zh: "选型入口",
              es: "Encuentre el producto adecuado",
              de: "Finden Sie das passende Produkt",
              fr: "Trouvez le produit adapté",
              pt: "Encontre o produto certo"
            })}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground">
              {pick(lang, {
              en: "Choose by application, filtration precision, or material grade to identify appropriate woven wire mesh, sintered media, and fabricated screen specifications.",
              zh: "按应用领域、过滤精度或材料牌号快速定位适用的编织网、烧结过滤介质及深加工筛网规格。",
              es: "Seleccione por aplicación, precisión de filtración o grado de material para identificar mallas tejidas, medios sinterizados y especificaciones fabricadas.",
              de: "Wählen Sie nach Anwendung, Filtrationsfeinheit oder Werkstoffgüte, um geeignete Drahtgewebe, Sintermedien und konfektionierte Siebe zu bestimmen.",
              fr: "Choisissez par application, précision de filtration ou nuance de matériau pour identifier les toiles tissées, médias frittés et spécifications façonnées.",
              pt: "Selecione por aplicação, precisão de filtração ou grau de material para identificar telas tecidas, meios sinterizados e especificações fabricadas."
            })}
            </p>
          </div>
        </Reveal>

        {/* 3-Card Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 md:gap-8">
          {finderCards.map((card, idx) => {
          const Icon = card.icon;
          return <Reveal key={card.id} delay={Math.min(idx * 80, 300)} className="h-full">
                <Card id={card.id} className="mf-card-hover group flex h-full flex-col justify-between rounded-lg border border-border bg-card p-6 md:p-8 scroll-mt-24 transition-colors hover:bg-muted/30">
                  <div>
                    {/* Icon Tile */}
                    <div className="flex size-11 items-center justify-center rounded-md border border-border bg-muted/60 text-primary">
                      <Icon className="size-5" />
                    </div>

                    <h3 className="mf-h3 mf-card-title mt-5 text-card-foreground">
                      {pick(lang, card.title)}
                    </h3>

                    {/* Shortened to one concise line */}
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                      {pick(lang, card.blurb)}
                    </p>
                  </div>

                  {/* Direct Links */}
                  <div className="mt-6 border-t border-border pt-4">
                    <ul className="space-y-1">
                      {card.links.map(link => <li key={link.to}>
                          <Link to={link.to} className="group/item flex min-h-[44px] items-center justify-between py-2 text-sm font-medium text-foreground transition-colors hover:text-primary">
                            <span>{pick(lang, link.label)}</span>
                            <ArrowRight className="mf-arrow-nudge size-4 shrink-0 text-muted-foreground transition-colors group-hover/item:text-primary" />
                          </Link>
                        </li>)}
                      {card.mutedLink && <li className="pt-1">
                          <Link to={card.mutedLink.to} className="group/muted flex min-h-[44px] items-center justify-between border-t border-dashed border-border/70 py-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80">
                            <span>{pick(lang, card.mutedLink.label)}</span>
                            <ArrowRight className="mf-arrow-nudge size-4 shrink-0 transition-colors" />
                          </Link>
                        </li>}
                    </ul>
                  </div>
                </Card>
              </Reveal>;
        })}
        </div>

        {/* Selection Guides Row */}
        <Reveal delay={240}>
          <div className="mt-10 rounded-lg border border-border bg-card p-6 md:p-7">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="mf-h3 text-card-foreground">
                  {pick(lang, {
                  en: "Selection Guides & Technical Resources",
                  zh: "选型指南与技术资料",
                  es: "Guías de selección y recursos técnicos",
                  de: "Auswahlleitfäden & Technische Ressourcen",
                  fr: "Guides de sélection et ressources techniques",
                  pt: "Guias de seleção e recursos técnicos"
                })}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {pick(lang, {
                  en: "Consult detailed engineering guides and technical references for mesh selection and specification.",
                  zh: "查阅详细的工程技术指南与产品选型基准参考资料。",
                  es: "Consulte guías de ingeniería detalladas y referencias técnicas para la selección y especificación.",
                  de: "Detaillierte Konstruktionsleitfäden und technische Referenzen für die Gewebeauswahl und Spezifikation einsehen.",
                  fr: "Consultez les guides techniques détaillés et les références d'ingénierie pour la sélection et les spécifications.",
                  pt: "Consulte guias de engenharia detalhados e referências técnicas para seleção e especificação."
                })}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2.5">
              {selectionGuides.map(guide => <Link key={guide.to} to={guide.to} className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-border bg-background px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-muted hover:text-primary">
                  <span>{pick(lang, guide.label)}</span>
                  <ArrowRight className="size-3.5 text-muted-foreground" />
                </Link>)}
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}